/* ============================================================
   TDHQ VARIANTS - the one engine behind the option picker.

   snippets/tdhq-option-picker renders the controls; this decides what they
   mean. The product page and the quick-view dialog both run it, so "which
   variant did the shopper choose", "which values are sold out", "which
   combinations do not exist" and "put the chosen id in the form" are answered
   in ONE place. Each surface then does its own painting (a product page also
   swaps the gallery, the sticky bar and the pickup line; a dialog does not)
   through the onChange callback.

   Usage:
     var picker = TdhqVariants.init(root, { onChange: function (variant, api) { ... } });
   root must contain the picker markup and the [data-pdp-variants] data.
   Returns null when there is nothing to drive (no data, or a single variant
   with no controls), so every caller must check.
   ============================================================ */
(function () {
  'use strict';

  function init(root, opts) {
    if (!root) { return null; }
    opts = opts || {};
    var dataEl = root.querySelector('[data-pdp-variants]') || document.querySelector('[data-pdp-variants]');
    if (!dataEl) { return null; }
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    if (!variants || !variants.length) { return null; }

    /* Three shapes of control, all rendered by the one picker snippet:
       - selects: the no-script fallback, still driven if a template uses it
       - radios : a single-option product posts radios named "id" directly
       - groups : one radio group per option on a multi-option product */
    var selects = [].slice.call(root.querySelectorAll('[data-pdp-option]'));
    var radios = [].slice.call(root.querySelectorAll('[data-pdp-vradio]'));
    var groups = [].slice.call(root.querySelectorAll('[data-pdp-optgroup]'));
    var idField = root.querySelector('[data-pdp-variant-id]');
    /* Script is running, so the hidden field is the one that posts. */
    if (idField) { idField.disabled = false; }

    function chosenValues() {
      if (!groups.length) { return selects.map(function (sel) { return sel.value; }); }
      return groups.map(function (g) {
        var fixed = g.getAttribute('data-pdp-fixed');
        if (fixed !== null) { return fixed; }
        var c = g.querySelector('input:checked');
        return c ? c.value : '';
      });
    }

    function chosenLabels() {
      return [].slice.call(root.querySelectorAll('.tdhq-opt')).map(function (g) {
        var c = g.querySelector('input:checked');
        var cur = g.querySelector('.tdhq-opt__cur');
        return c ? (c.getAttribute('data-label') || c.value) : (cur ? cur.textContent : '');
      }).filter(Boolean);
    }

    /* Active ring, the "Colour: Black" read-out, and - per value - whether it
       exists with the other choices (is-na) and is in stock (is-out). */
    function paint() {
      [].slice.call(root.querySelectorAll('.tdhq-opt')).forEach(function (g) {
        var c = g.querySelector('input:checked');
        var cur = g.querySelector('[data-pdp-optcur]');
        if (c && cur) { cur.textContent = c.getAttribute('data-label') || c.value; }
        [].slice.call(g.querySelectorAll('input[type="radio"]')).forEach(function (inp) {
          var lab = inp.closest('.tdhq-opt__v');
          if (lab) { lab.classList.toggle('is-active', inp.checked); }
        });
      });
      if (!groups.length) { return; }
      var chosen = chosenValues();
      groups.forEach(function (g, gi) {
        [].slice.call(g.querySelectorAll('input[type="radio"]')).forEach(function (inp) {
          var lab = inp.closest('.tdhq-opt__v');
          if (!lab) { return; }
          var exists = false, avail = false;
          for (var i = 0; i < variants.length && !avail; i++) {
            var vo = variants[i].options;
            var ok = vo[gi] === inp.value;
            for (var n = 0; ok && n < chosen.length; n++) { if (n !== gi && vo[n] !== chosen[n]) { ok = false; } }
            if (ok) { exists = true; if (variants[i].available) { avail = true; } }
          }
          lab.classList.toggle('is-out', exists && !avail);
          lab.classList.toggle('is-na', !exists);
          /* what a screen reader hears follows the same state */
          var sr = lab.querySelector('.tdhq-opt__sr');
          if (sr) { sr.textContent = !exists ? ', not available with these options' : (!avail ? ', sold out' : ''); }
        });
      });
    }

    function match() {
      if (radios.length) {
        var picked = null;
        for (var r = 0; r < radios.length; r++) { if (radios[r].checked) { picked = radios[r].value; break; } }
        for (var j = 0; j < variants.length; j++) { if (String(variants[j].id) === String(picked)) { return variants[j]; } }
        return null;
      }
      var chosen = chosenValues();
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var n = 0; n < chosen.length; n++) { if (v.options[n] !== chosen[n]) { ok = false; break; } }
        if (ok) { return v; }
      }
      return null;
    }

    var api = {
      variants: variants,
      groups: groups,
      chosenValues: chosenValues,
      chosenLabels: chosenLabels,
      paint: paint,
      match: match,
      sync: sync
    };

    function sync() {
      paint();
      var v = match();
      if (v && idField) { idField.value = v.id; }
      if (typeof opts.onChange === 'function') { opts.onChange(v, api); }
      return v;
    }

    selects.forEach(function (sel) { sel.addEventListener('change', sync); });
    radios.forEach(function (r) { r.addEventListener('change', sync); });

    /* A pick that makes an impossible combination (Black in a size that only
       comes in Sand) keeps the value just picked and moves the OTHER options
       to the nearest variant that has it, in stock first - never a dead
       "Unavailable" button. */
    groups.forEach(function (g, gi) {
      g.addEventListener('change', function (e) {
        if (!match() && e.target && e.target.value !== undefined) {
          var val = e.target.value, best = null;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].options[gi] !== val) { continue; }
            if (!best || (!best.available && variants[i].available)) { best = variants[i]; }
            if (best.available) { break; }
          }
          if (best) {
            groups.forEach(function (g2, gj) {
              if (gj === gi) { return; }
              [].slice.call(g2.querySelectorAll('input[type="radio"]')).forEach(function (r2) {
                if (r2.value === best.options[gj]) { r2.checked = true; }
              });
            });
          }
        }
        sync();
      });
    });

    paint();
    return api;
  }

  /* ------------------------------------------------------------
     A PICTURE CHANGE THAT FADES, NEVER BLINKS.

     Client: "changing picture on thumbnail looks super bad, no animation"
     (audit MOT-09: the stage source changed between two frames with no
     transition). The new picture has already been decoded by the caller, so
     it is put in the frame at once, and a copy of the OLD picture is laid
     over it and faded out: both pictures are on screen for the whole 220ms,
     so there is never a blank frame. The copy takes the old picture's exact
     box, fit and framing transform, and is not marked for the framing
     script, which only ever sees the real picture.

     Used by the product page gallery, its lightbox and quick view.
     `swap` does the real change (src, srcset, framing reset).
     ------------------------------------------------------------ */
  function fadeSwap(img, swap) {
    var parent = img && img.parentNode;
    var still = !window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    [].slice.call(parent ? parent.querySelectorAll('[data-tdhq-ghost]') : []).forEach(function (g) { g.remove(); });
    if (still || !parent || !img.animate || !img.offsetWidth || !img.complete || !img.naturalWidth) { swap(); return; }
    var cs = window.getComputedStyle(img);
    var g = img.cloneNode(false);
    ['id', 'data-tdhq-fit', 'srcset', 'sizes', 'fetchpriority', 'loading', 'class'].forEach(function (a) { g.removeAttribute(a); });
    g.src = img.currentSrc || img.src;
    g.alt = '';
    g.setAttribute('aria-hidden', 'true');
    g.setAttribute('data-tdhq-ghost', '');
    g.style.cssText = [
      'position:absolute', 'margin:0', 'pointer-events:none', 'max-width:none', 'max-height:none',
      'left:' + img.offsetLeft + 'px', 'top:' + img.offsetTop + 'px',
      'width:' + img.offsetWidth + 'px', 'height:' + img.offsetHeight + 'px',
      'box-sizing:' + cs.boxSizing, 'padding:' + cs.padding, 'border-radius:' + cs.borderRadius,
      'object-fit:' + cs.objectFit, 'object-position:' + cs.objectPosition,
      'transform:' + cs.transform, 'transform-origin:' + cs.transformOrigin,
      'background:' + cs.backgroundColor
    ].join(';');
    img.insertAdjacentElement('afterend', g);
    swap();
    var a = g.animate([{ opacity: 1 }, { opacity: 0 }], { duration: (window.TdhqMotion ? window.TdhqMotion.ms('--tdhq-t', 220) : 220), easing: (window.TdhqMotion ? window.TdhqMotion.easing() : 'cubic-bezier(0.4, 0, 0.2, 1)') });
    a.onfinish = a.oncancel = function () { g.remove(); };
  }

  window.TdhqVariants = { init: init, fadeSwap: fadeSwap };

  /* ------------------------------------------------------------
     SIZE GUIDE - one delegated opener for every picker on the page.

     The guide's dialog is rendered by the picker (snippets/tdhq-size-guide),
     so it arrives wherever a picker does. It used to carry its own inline
     script, and quick view inserts its markup with DOMParser, which never runs
     a script: on a listing the quick view's "Size guide" button did nothing,
     and on a product page the page's binder opened the PAGE's guide - another
     brand's - from the quick view (audit pdp-03, measured both ways). One
     listener on the document covers a picker that arrives later, and each
     button opens the dialog it names (aria-controls), which is unique per
     picker.
     ------------------------------------------------------------ */
  var sgOpener = null;

  function sgDialogFor(btn) {
    var id = btn.getAttribute('aria-controls');
    var dlg = id ? document.getElementById(id) : null;
    if (dlg) { return dlg; }
    var scope = btn.closest('[data-tdhq-qv]') || document;
    return scope.querySelector('dialog[data-sg]');
  }

  function sgBind(dlg) {
    if (dlg.hasAttribute('data-sg-bound')) { return; }
    dlg.setAttribute('data-sg-bound', '');
    /* Keyboard users land back on the button they pressed. */
    dlg.addEventListener('close', function () {
      var back = sgOpener;
      sgOpener = null;
      if (back && back.focus && document.contains(back)) { back.focus({ preventScroll: true }); }
    });
  }

  /* U.27: cm or inches (kept on this device), and the size picked in the
     buy box marked in the chart, both set as the guide opens. */
  function sgUnit(dlg, unit) {
    if (unit !== 'in') { unit = 'cm'; }
    dlg.setAttribute('data-unit', unit);
    var bs = dlg.querySelectorAll('[data-sg-unit]');
    for (var i = 0; i < bs.length; i++) {
      bs[i].setAttribute('aria-pressed', bs[i].getAttribute('data-sg-unit') === unit ? 'true' : 'false');
    }
  }

  function sgPrepare(dlg, opener) {
    if (dlg.querySelector('[data-sg-unit]')) {
      var unit = 'cm';
      try { unit = window.localStorage.getItem('tdhq-sg-unit') || 'cm'; } catch (err) {}
      sgUnit(dlg, unit);
    }
    var group = opener.closest('.tdhq-opt');
    var cur = group ? group.querySelector('[data-pdp-optcur]') : null;
    var size = cur ? cur.textContent.replace(/\s+/g, ' ').trim() : '';
    var rows = dlg.querySelectorAll('tr[data-sg-size]');
    for (var i = 0; i < rows.length; i++) {
      rows[i].classList.toggle('is-current', size !== '' && rows[i].getAttribute('data-sg-size') === size);
    }
  }

  function sgClose(dlg) {
    if (!dlg) { return; }
    if (dlg.close) { dlg.close(); } else { dlg.removeAttribute('open'); }
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) { return; }

    var opener = t.closest('[data-sg-open]');
    if (opener) {
      var dlg = sgDialogFor(opener);
      if (!dlg) { return; }
      e.preventDefault();
      sgBind(dlg);
      sgOpener = opener;
      sgPrepare(dlg, opener);
      if (dlg.open) { return; }
      if (dlg.showModal) { dlg.showModal(); } else { dlg.setAttribute('open', ''); }
      return;
    }

    var unitBtn = t.closest('[data-sg-unit]');
    if (unitBtn) {
      var unitDlg = unitBtn.closest('dialog[data-sg]');
      var unitVal = unitBtn.getAttribute('data-sg-unit');
      if (unitDlg) { sgUnit(unitDlg, unitVal); }
      try { window.localStorage.setItem('tdhq-sg-unit', unitVal); } catch (err) {}
      return;
    }

    if (t.closest('[data-sg-close]')) { sgClose(t.closest('dialog')); return; }

    /* "Ask about sizing" on the product page: the page's own Ask a question
       popup, with the sizing question started, in place of a link to another
       page (client, 2026-09-16: ask must open a popup, never send the shopper
       away). */
    var ask = t.closest('[data-sg-ask]');
    if (ask) {
      var askDlg = document.getElementById(ask.getAttribute('data-sg-ask'));
      if (!askDlg || !askDlg.showModal) { return; }
      e.preventDefault();
      var back = sgOpener;
      sgOpener = null;
      sgClose(ask.closest('dialog'));
      var box = askDlg.querySelector('textarea');
      if (box && !box.value.trim()) { box.value = 'Sizing question: I usually wear size '; }
      if (!askDlg.open) { askDlg.showModal(); }
      if (box) {
        box.focus({ preventScroll: true });
        try { box.setSelectionRange(box.value.length, box.value.length); } catch (err) {}
      }
      if (back) {
        askDlg.addEventListener('close', function () {
          if (document.contains(back) && back.focus) { back.focus({ preventScroll: true }); }
        }, { once: true });
      }
      return;
    }

    /* A click on the backdrop lands on the dialog element itself, never on
       its content. */
    if (t.matches && t.matches('dialog[data-sg]')) { sgClose(t); }
  });
})();
